import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn } from '../ui/FadeIn';
import { CheckCircle2Icon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const categories = [{
  name: 'Inflamación y Dolores',
  desc: 'Articulaciones, músculos, rigidez, dolor por frío o humedad.',
  count: 30,
  image: 'https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=600&h=700&fit=crop&q=80'
}, {
  name: 'Digestión',
  desc: 'Gases, hinchazón, acidez, estreñimiento y náuseas.',
  count: 90,
  image: 'https://images.unsplash.com/photo-1556679343-c7306c1976bc?w=600&h=700&fit=crop&q=80'
}, {
  name: 'Ansiedad y Sueño',
  desc: 'Insomnio, nervios, estrés y mente acelerada.',
  count: 30,
  image: 'https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=600&h=700&fit=crop&q=80'
}, {
  name: 'Defensas y Resfríos',
  desc: 'Gripe, tos, garganta y defensas bajas.',
  count: 60,
  image: 'https://images.unsplash.com/photo-1615485290382-441e4d049cb5?w=600&h=700&fit=crop&q=80'
}, {
  name: 'Metabolismo y Peso',
  desc: 'Líquidos, azúcar, grasa y metabolismo lento.',
  count: 30,
  image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600&h=700&fit=crop&q=80'
}, {
  name: 'Articulaciones y Huesos',
  desc: 'Artritis, rigidez, desgaste y circulación.',
  count: 30,
  image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600&h=700&fit=crop&q=80'
}, {
  name: 'Piel y Circulación',
  desc: 'Piel seca, manchas, várices y piernas pesadas.',
  count: 30,
  image: 'https://images.unsplash.com/photo-1596755389378-c31d21fd1273?w=600&h=700&fit=crop&q=80'
}];
export function RecipeCategoriesSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const goTo = useCallback((index: number) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  }, [current]);
  const next = useCallback(() => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % categories.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + categories.length) % categories.length);
  }, []);
  useEffect(() => {
    const timer = setInterval(next, 4000);
    return () => clearInterval(timer);
  }, [next]);
  const cat = categories[current];
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0
    })
  };
  return <section className="bg-ancestral-white py-14 md:py-20 text-ancestral-dark" data-id="element-250">
      <div className="container mx-auto px-4" data-id="element-251">
        <div className="max-w-5xl mx-auto" data-id="element-252">
          {/* Header */}
          <FadeIn className="text-center mb-10" data-id="element-253">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-ancestral-green mb-2" data-id="element-254">
              ¿Qué Obtienes Con El Recetario?
            </h2>
            <p className="text-base md:text-lg text-ancestral-dark/60" data-id="element-255">
              300 recetas en 7 categorías, listas para usar desde el día 1
            </p>
          </FadeIn>

          {/* Carousel */}
          <FadeIn delay={0.15} data-id="element-256">
            <div className="relative max-w-sm mx-auto mb-10" data-id="element-257">
              {/* Image + info card */}
              <div className="relative overflow-hidden rounded-2xl bg-white shadow-lg border border-gray-100" data-id="element-258">
                <div className="relative aspect-[4/5] overflow-hidden" data-id="element-259">
                  <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-260">
                    <motion.img key={current} src={cat.image} alt={cat.name} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                    duration: 0.35,
                    ease: 'easeInOut'
                  }} className="absolute inset-0 w-full h-full object-cover" data-id="element-261" />
                  </AnimatePresence>

                  {/* Recipe count badge */}
                  <div className="absolute top-4 right-4 bg-ancestral-green/90 backdrop-blur-sm text-ancestral-gold px-3 py-1.5 rounded-full z-10" data-id="element-262">
                    <span className="text-sm font-bold" data-id="element-263">
                      {cat.count} recetas
                    </span>
                  </div>
                </div>

                {/* Text below image */}
                <AnimatePresence mode="wait" data-id="element-264">
                  <motion.div key={current} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -10
                }} transition={{
                  duration: 0.25
                }} className="p-5" data-id="element-265">
                    <h3 className="font-serif font-bold text-xl text-ancestral-green mb-1" data-id="element-266">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-ancestral-dark/60 leading-relaxed" data-id="element-267">
                      {cat.desc}
                    </p>
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation arrows */}
              <button onClick={prev} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 bg-white shadow-md border border-gray-200 rounded-full p-2 hover:bg-gray-50 transition-colors z-20" aria-label="Anterior" data-id="element-268">
                <ChevronLeftIcon className="w-5 h-5 text-ancestral-green" data-id="element-269" />
              </button>
              <button onClick={next} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 bg-white shadow-md border border-gray-200 rounded-full p-2 hover:bg-gray-50 transition-colors z-20" aria-label="Siguiente" data-id="element-270">
                <ChevronRightIcon className="w-5 h-5 text-ancestral-green" data-id="element-271" />
              </button>
            </div>

            {/* Dot indicators */}
            <div className="flex justify-center gap-2 mb-10" data-id="element-272">
              {categories.map((_, idx) => <button key={idx} onClick={() => goTo(idx)} className={`rounded-full transition-all duration-300 ${idx === current ? 'w-8 h-2.5 bg-ancestral-green' : 'w-2.5 h-2.5 bg-ancestral-green/25 hover:bg-ancestral-green/40'}`} aria-label={`Ir a ${categories[idx].name}`} data-id="element-273" />)}
            </div>
          </FadeIn>

          {/* Features bar */}
          <FadeIn delay={0.3} data-id="element-274">
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-ancestral-dark/70 font-medium" data-id="element-275">
              {['Ingredientes exactos', 'Paso a paso', 'Menos de 10 min', 'Sin efectos secundarios'].map((f, i) => <span key={i} className="flex items-center gap-1.5" data-id="element-276">
                  <CheckCircle2Icon className="w-4 h-4 text-ancestral-gold" data-id="element-277" />
                  {f}
                </span>)}
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}